import { ref } from 'vue';

/**
 * [DEV ONLY] 최근 설정한 키를 추적하는 공통 로직.
 * clearUserEnvValue 콜백을 받아, DevTool에서 일괄 삭제 시 사용한다.
 * production에서는 no-op.
 *
 * @param clearUserEnvValue - (key, all?, global?) => Promise<boolean>
 * @returns 추적용 메서드/상태 객체
 */
export function createDevOnlyKeyTracker(
  clearUserEnvValue: (key: string, all?: boolean, global?: boolean) => Promise<boolean>,
) {
  const recentlySetKeys = ref<Set<string>>(new Set());

  const trackKey = (key: string) => {
    if (import.meta.env.DEV) {
      recentlySetKeys.value.add(key);
    }
  };

  const getRecentlySetKeys = () => {
    if (!import.meta.env.DEV) return [];
    return Array.from(recentlySetKeys.value);
  };

  const removeFromRecentlySet = (key: string) => {
    if (!import.meta.env.DEV) return;
    recentlySetKeys.value.delete(key);
  };

  const clearRecentlySetKeys = async (keys?: string[]) => {
    if (!import.meta.env.DEV) return true;
    const targetKeys = keys ?? Array.from(recentlySetKeys.value);
    const results = await Promise.all(
      targetKeys.map((key) => clearUserEnvValue(key, false, key.includes('global'))),
    );
    targetKeys.forEach((key, idx) => {
      if (results[idx]) {
        recentlySetKeys.value.delete(key);
      }
    });
    return results.every(Boolean);
  };

  const clearAll = () => {
    recentlySetKeys.value.clear();
  };

  return {
    recentlySetKeys,
    trackKey,
    getRecentlySetKeys,
    removeFromRecentlySet,
    clearRecentlySetKeys,
    clearAll,
  };
}
